import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import { Subscription } from "rxjs";
import { WorkflowService } from "../../service/workflow.service";
import {
  EntrySignalGroup,
  EntrySignalOption,
  ReasonCodeOption,
  entrySignalTooltip,
} from "./s09-workflow.models";

@Component({
  selector: "app-s09-entry-signals",
  templateUrl: "./s09-entry-signals.component.html",
  styleUrls: ["./s09-workflow.scss"],
  standalone: false,
})
export class S09EntrySignalsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() brandKey: number | null = 200104;

  groups: EntrySignalGroup[] = [];
  reasonCodes: ReasonCodeOption[] = [];
  loading = false;
  saving = false;
  hint = "";
  filter = "";
  reasonFilter = "";
  private loadedOnce = false;

  selectedGroupId: number | null = null;
  groupDraft = this.emptyGroup();
  signalDraft = this.emptySignal();
  editingSignalId: number | null = null;

  private subs: Subscription[] = [];

  constructor(private workflowService: WorkflowService) {}

  ngOnChanges(ch: SimpleChanges): void {
    if (ch["brandKey"]) {
      this.loadedOnce = true;
      this.reload();
    }
  }

  ngOnInit(): void {
    if (!this.loadedOnce) this.reload();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  get selectedGroup(): EntrySignalGroup | null {
    return (
      this.groups.find((g) => g.group_id === this.selectedGroupId) || null
    );
  }

  get filteredGroups(): EntrySignalGroup[] {
    const q = this.filter.trim().toLowerCase();
    if (!q) return this.groups;
    return this.groups.filter((g) => {
      const hay = `${g.group_code} ${g.group_label}`.toLowerCase();
      return hay.includes(q);
    });
  }

  get filteredReasons(): ReasonCodeOption[] {
    const q = this.reasonFilter.trim().toLowerCase();
    const used = new Set(
      (this.selectedGroup?.signals || []).map((s) =>
        s.signal_code.toUpperCase()
      )
    );
    return this.reasonCodes.filter((r) => {
      if (
        this.editingSignalId == null &&
        used.has(r.reason_code.toUpperCase())
      ) {
        return false; // hide already-added codes when adding new
      }
      if (!q) return true;
      const hay = `${r.reason_code} ${r.reason_desc || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }

  tip(s: EntrySignalOption): string {
    return entrySignalTooltip(s);
  }

  reload(): void {
    const brand = Number(this.brandKey) || 0;
    if (!brand) {
      this.groups = [];
      this.hint = "No brand selected.";
      return;
    }
    this.loading = true;
    this.hint = "";
    const sub1 = this.workflowService.getEntrySignals(brand).subscribe((rows) => {
      this.loading = false;
      this.groups = rows || [];
      if (!this.groups.length) {
        this.hint = "No signal groups yet. Create a group, then add reason codes.";
      }
      if (this.selectedGroupId) {
        const found = this.groups.find((g) => g.group_id === this.selectedGroupId);
        if (found) this.selectGroup(found);
        else if (this.groups.length) this.selectGroup(this.groups[0]);
        else this.newGroup();
      } else if (this.groups.length) {
        this.selectGroup(this.groups[0]);
      } else {
        this.newGroup();
      }
    });
    this.subs.push(sub1);

    const sub2 = this.workflowService.getReasonCodes(brand).subscribe((rows) => {
      this.reasonCodes = rows || [];
    });
    this.subs.push(sub2);
  }

  selectGroup(g: EntrySignalGroup): void {
    this.selectedGroupId = g.group_id;
    this.groupDraft = {
      group_id: g.group_id,
      group_code: g.group_code,
      group_label: g.group_label,
      sort_order: g.sort_order || 0,
    };
    this.cancelSignalEdit();
  }

  newGroup(): void {
    this.selectedGroupId = null;
    this.groupDraft = this.emptyGroup();
    this.cancelSignalEdit();
  }

  saveGroup(): void {
    const brand = Number(this.brandKey) || 0;
    const code = (this.groupDraft.group_code || "").trim();
    if (!brand || !code) {
      this.hint = "Group code is required.";
      return;
    }
    this.saving = true;
    const sub = this.workflowService
      .saveEntrySignalGroup({
        brand_key: brand,
        group_id: this.groupDraft.group_id,
        group_code: code,
        group_label: (this.groupDraft.group_label || "").trim() || code,
        sort_order: this.groupDraft.sort_order || 0,
        is_active: true,
      })
      .subscribe((saved) => {
        this.saving = false;
        if (!saved) {
          this.hint = "Save group failed.";
          return;
        }
        this.selectedGroupId = saved.group_id;
        this.hint = `Saved group ${saved.group_code}.`;
        this.reload();
      });
    this.subs.push(sub);
  }

  deleteGroup(): void {
    const brand = Number(this.brandKey) || 0;
    const id = this.selectedGroupId || this.groupDraft.group_id;
    if (!brand || !id) return;
    if (!confirm(`Delete group “${this.groupDraft.group_label || id}” and its codes?`))
      return;
    const sub = this.workflowService
      .deleteEntrySignalGroup(brand, id)
      .subscribe((ok) => {
        if (ok) {
          this.selectedGroupId = null;
          this.reload();
        }
      });
    this.subs.push(sub);
  }

  startAddSignal(): void {
    this.editingSignalId = null;
    this.signalDraft = this.emptySignal();
    this.reasonFilter = "";
  }

  startEditSignal(s: EntrySignalOption): void {
    this.editingSignalId = s.signal_id;
    this.signalDraft = {
      signal_id: s.signal_id,
      signal_code: s.signal_code,
      signal_label: s.signal_label || "",
      sort_order: s.sort_order || 0,
    };
    this.reasonFilter = "";
  }

  cancelSignalEdit(): void {
    this.editingSignalId = null;
    this.signalDraft = this.emptySignal();
    this.reasonFilter = "";
  }

  onReasonPick(code: string): void {
    const r = this.reasonCodes.find(
      (x) => x.reason_code.toUpperCase() === code.toUpperCase()
    );
    this.signalDraft.signal_code = r?.reason_code || code;
    this.signalDraft.signal_label = r?.reason_desc || code;
  }

  saveSignal(): void {
    const brand = Number(this.brandKey) || 0;
    const groupId = this.selectedGroupId || this.groupDraft.group_id;
    const code = (this.signalDraft.signal_code || "").trim();
    if (!brand || !groupId) {
      this.hint = "Save the group first, then add reason codes.";
      return;
    }
    if (!code) {
      this.hint = "Select a reason code.";
      return;
    }
    this.saving = true;
    const sub = this.workflowService
      .saveEntrySignal({
        brand_key: brand,
        group_id: groupId,
        signal_id: this.signalDraft.signal_id,
        signal_code: code,
        signal_label: (this.signalDraft.signal_label || "").trim() || code,
        sort_order: this.signalDraft.sort_order || 0,
        is_active: true,
      })
      .subscribe((saved) => {
        this.saving = false;
        if (!saved) {
          this.hint = "Save signal failed.";
          return;
        }
        this.hint = `Saved ${saved.signal_code}.`;
        this.cancelSignalEdit();
        this.reload();
      });
    this.subs.push(sub);
  }

  deleteSignal(s: EntrySignalOption): void {
    const brand = Number(this.brandKey) || 0;
    if (!brand || !s.signal_id) return;
    if (!confirm(`Remove reason code ${s.signal_code} from this group?`)) return;
    const sub = this.workflowService
      .deleteEntrySignal(brand, s.signal_id)
      .subscribe((ok) => {
        if (ok) {
          if (this.editingSignalId === s.signal_id) this.cancelSignalEdit();
          this.reload();
        }
      });
    this.subs.push(sub);
  }

  private emptyGroup(): {
    group_id?: number;
    group_code: string;
    group_label: string;
    sort_order: number;
  } {
    return { group_code: "", group_label: "", sort_order: 0 };
  }

  private emptySignal(): {
    signal_id?: number;
    signal_code: string;
    signal_label: string;
    sort_order: number;
  } {
    return { signal_code: "", signal_label: "", sort_order: 0 };
  }
}
